.wrapper {
  outline: none;
  border: none;
}

.dropLine {
  height: 4px;
  border-radius: var(--tuna-shape-radius-sm);
  margin-top: 4px;
  margin-bottom: 4px;
  pointer-events: none;
  background: var(--tuna-palette-primary-main);
  box-shadow: 0 0 8px color-mix(in oklch, var(--tuna-palette-primary-main) 40%, transparent);
  animation: dropLinePulse 1.2s ease-in-out infinite;
}

@keyframes dropLinePulse {
  0%,
  100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}

.block {
  position: relative;
  align-items: center;
  vertical-align: bottom;
  isolation: isolate;
}

/* Extend the block's hover region 16px to the left to bridge the dead zone
 * between the block and its gutter handle. Without this, moving the cursor
 * from block content toward the handle crosses an empty 16px strip where
 * the block loses :hover and the handle fades to opacity: 0 mid-reach. */
.block::before {
  content: '';
  position: absolute;
  top: 0;
  left: -16px;
  width: 16px;
  height: 100%;
}

/* Native ::selection on text glyphs uses the same selection token as block-level
 * range bars and per-element selected styling — one hue at one intensity across the
 * whole editor. The natural text color is preserved so glyphs stay readable on the
 * translucent background. */
.block ::selection {
  background-color: var(--tuna-palette-selection);
  color: var(--tuna-palette-primary-contrastText);
}

/* Block-level range-selection feedback intentionally omitted.
 *
 * `useIsInSelectionRange` continues to set `data-in-range='true'` on blocks strictly
 * inside a multi-block selection, but no styling is bound to it — multi-block range
 * painting is the least-valuable selection affordance for Tunasong and its backing
 * subscription chain (selection-store ↔ slate-yjs onChange wrapper) has a known
 * staleness bug. Removing only the visual leaves the JS path intact so the bug can
 * be fixed without re-introducing styling. Focus state still differentiates via
 * `.block--focused` (4px right border); section-level cursor presence via
 * `.section--selected` in plugins/section/src/block.module.css. */

.block--inline {
  display: inline-flex;
}

.block--border {
  border-style: solid;
  border-color: var(--tuna-palette-action-disabled);
  border-radius: var(--tuna-shape-borderRadius, 4px);
  border-width: 0.8px;
}

.block--focused {
  transition: border-color 0.3s;
  border-color: var(--tuna-palette-primary-main);
  border-right-width: 4px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.block--dragOver {
  background: color-mix(in oklch, var(--tuna-palette-primary-main) 4%, transparent);
  border-radius: var(--tuna-shape-borderRadius, 4px);
}

.block--focusRight {
  transition: border-color 0.3s;
  border-top-style: none;
  border-bottom-style: none;
  border-left-style: none;
  border-right-style: solid;
  border-top-width: 0;
  border-bottom-width: 0;
  border-left-width: 0;
  border-right-width: 4px;
  border-right-color: var(--tuna-palette-primary-main);
  border-radius: 0;
}

/* ============================================================
 * Personal-note anchor highlights
 *
 * Three states layer onto the same visual axis (left-edge inset shadow), so
 * stronger states override weaker ones via cascade order:
 *   anchored ≺ anchored-hover ≺ anchored-active
 * The active state additionally exposes `--anchor-active: 1` so child
 * elements (anchor markers) can interpolate their own appearance against the
 * same source of truth.
 * ============================================================ */

/* Subtle baseline halo for any block that is the target of at least one
 * personal note. Communicates "this block has notes attached" while the My
 * Notes panel is open without competing with the user's primary content. */
.block[data-anchored='true'] {
  box-shadow: inset 2px 0 0 0 color-mix(in oklch, var(--tuna-palette-selection) 25%, transparent);
}

/* Hover preview from the sidebar — slightly stronger than the baseline halo
 * so the user can scan rows and see "this is where this note points" without
 * activating the row. Cleared on mouseleave/blur. */
.block[data-anchored-hover='true'] {
  box-shadow: inset 3px 0 0 0 color-mix(in oklch, var(--tuna-palette-selection) 55%, transparent);
}

/* Active state — the user expanded the note row that targets this block. The
 * left-edge accent uses the full selection color (matching the row's accent),
 * and the CSS variable signals descendants (markers, etc.) to lift their own
 * presentation. */
.block[data-anchored-active='true'] {
  box-shadow: inset 3px 0 0 0 var(--tuna-palette-selection);
  --anchor-active: 1;
}

/* One-shot 600ms spotlight applied on activation. The component toggles
 * `data-anchored-pulse` for 600ms then removes it. Reduced-motion users skip
 * the keyframe entirely — they still see the static halo from the active
 * state, just no animated pulse. */
@media (prefers-reduced-motion: no-preference) {
  .block[data-anchored-pulse='true'] {
    animation: anchorSpotlight 600ms ease-out;
  }

  @keyframes anchorSpotlight {
    0% {
      background-color: color-mix(in oklch, var(--tuna-palette-selection) 22%, transparent);
    }
    100% {
      background-color: transparent;
    }
  }
}
